export interface ChartPricePoint {
  readonly date: string;
  readonly price: number;
}

export interface UnderlyingHoverMeta {
  readonly sourceIndex: number;
  readonly name: string;
  readonly history: readonly ChartPricePoint[];
  readonly basePrice: number | null;
  readonly priceMin: number | null;
  readonly priceMax: number | null;
}

export const getTimeMs = (date: string): number => new Date(date).getTime();

export const findNearestIndexByTime = (times: readonly number[], target: number): number => {
  if (times.length === 0) {
    return -1;
  }

  let low = 0;
  let high = times.length - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (times[mid] === target) {
      return mid;
    }
    if (times[mid] < target) {
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  if (low <= 0) {
    return 0;
  }
  if (low >= times.length) {
    return times.length - 1;
  }

  const before = low - 1;
  return target - times[before] <= times[low] - target ? before : low;
};

export const interpolateTimeAlongSampledPoints = (
  sampledTimes: readonly number[],
  relativeT: number
): number => {
  if (sampledTimes.length === 0) {
    return 0;
  }
  if (sampledTimes.length === 1) {
    return sampledTimes[0];
  }

  const clamped = Math.min(1, Math.max(0, relativeT));
  // chart-kit uses xMax = point count, so the last vertex sits at (n-1)/n of the plot width.
  const sampledFloat = clamped * sampledTimes.length;
  const lastIndex = sampledTimes.length - 1;
  if (sampledFloat >= lastIndex) {
    return sampledTimes[lastIndex];
  }
  const index = Math.min(Math.floor(sampledFloat), sampledTimes.length - 2);
  const fraction = sampledFloat - index;
  return sampledTimes[index] + fraction * (sampledTimes[index + 1] - sampledTimes[index]);
};

export const mapTimeToSampledChartX = (
  sampledTimes: readonly number[],
  dayTime: number,
  paddingLeft: number,
  graphWidth: number
): number => {
  if (sampledTimes.length === 0) {
    return paddingLeft;
  }
  if (sampledTimes.length === 1) {
    return paddingLeft;
  }
  if (dayTime <= sampledTimes[0]) {
    return paddingLeft;
  }

  const last = sampledTimes[sampledTimes.length - 1];
  if (dayTime >= last) {
    return paddingLeft + graphWidth;
  }

  let index = 0;
  while (index < sampledTimes.length - 1 && sampledTimes[index + 1] < dayTime) {
    index += 1;
  }

  const start = sampledTimes[index];
  const end = sampledTimes[index + 1];
  const fraction = end === start ? 0 : (dayTime - start) / (end - start);
  const sampledFloat = index + fraction;
  return paddingLeft + (sampledFloat / sampledTimes.length) * graphWidth;
};

export const findClosestPriceAtOrBefore = (
  points: readonly ChartPricePoint[],
  targetTime: number
): ChartPricePoint | null => {
  if (points.length === 0) {
    return null;
  }

  const times = points.map((point) => getTimeMs(point.date));
  const nearest = findNearestIndexByTime(times, targetTime);
  if (nearest < 0) {
    return null;
  }
  if (times[nearest] <= targetTime) {
    return points[nearest];
  }
  if (nearest === 0) {
    return points[0];
  }
  return points[nearest - 1];
};
